import { Helper, Icon, IconButton, Input, TextEditor } from '@smartgift/frontend-toolkit';
import styled, { css } from 'styled-components';

const RecipientExperiencePreviewWrapper = styled.div``;

const RecipientExperiencePreviewHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const PreviewHeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
`;

const DeviceWrapper = styled.div`
  display: flex;
  gap: 4px;
`;

const DeviceButton = styled(IconButton)`
  color: var(--color-fg-weaker);

  ${({ isActive }) =>
    isActive &&
    css`
      background-color: var(--color-bg-weaker);
      border: 1px solid var(--color-border-default);
      border-radius: var(--size-border-radius-md);
      color: var(--color-fg-default);
    `}

  &:hover {
    background-color: var(--color-bg-weak);
    border: 1px solid var(--color-border-default);
    border-radius: var(--size-border-radius-md);
  }

  height: 30px;
  transition: none;
  width: 40px;
`;

const RecipientExperiencePreviewBodyFrame = styled.div`
  border: 1px solid var(--color-border-default);
  border-radius: var(--size-border-radius-md);
  margin-top: 12px;
`;

const RecipientExperiencePreviewBody = styled.div`
  align-items: center;
  background-image: url(${({ wrapperUrl }) => wrapperUrl});
  background-position: center;
  background-size: contain;
  display: flex;
  height: 550px;
  justify-content: center;
  position: relative;
  width: 100%;

  ${({ isMobile }) =>
    isMobile &&
    css`
      margin: 0 auto 0 auto;
      width: 422px;
    `}
`;

const RecipientExperiencePreviewBodyContent = styled.div`
  align-items: center;
  background-color: var(--color-bg-default);
  border: 1px solid var(--color-border-default);
  border-radius: var(--size-border-radius-md);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 16px 80px;
  position: relative;
  width: 560px;

  .logo {
    position: absolute;
    right: 24px;
    top: 16px;
  }

  ${({ isMobile }) =>
    isMobile &&
    css`
      padding: 16px;
      width: 390px;

      .logo {
        right: 12px;
        top: 12px;
      }
    `}
`;

const UploadImageButtonWrapper = styled.div`
  border: 1px solid var(--color-border-default);
  border-radius: var(--size-border-radius-lg);
  height: 100%;
  opacity: 0.4;
  padding: 6px;
  width: 180px;

  ${({ error }) =>
    error &&
    css`
      border: 2px solid var(--color-error-default);
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      &,
      ${UploadButton} {
        opacity: 0.4;
      }

      :hover {
        border-color: var(--color-border-default);
      }
    `}
`;

const UploadButton = styled.div`
  align-items: center;
  background-color: transparent;
  border: 2px dashed var(--color-border-strong);
  border-radius: var(--size-border-radius-md);
  color: var(--color-accent-secondary-default);
  display: flex;
  flex-direction: column;
  font-size: 14px;
  font-weight: 700;
  height: 100%;
  justify-content: center;
  text-decoration: none;
  white-space: nowrap;
  width: 100%;

  :hover {
    background-color: transparent;
    color: var(--color-accent-secondary-default);
  }
`;

const PreviewItem = styled.div`
  margin-bottom: 24px;
  position: relative;

  &:first-of-type {
    margin-top: 24px;
  }
`;

const PreviewItemIconButton = styled(IconButton)`
  background-color: var(--color-bg-stronger);
  padding: 15px;
  position: absolute;
  right: ${({ right }) => (right ? `${right}px` : 0)};
  top: ${({ top }) => (top ? `${top}px` : 0)};
  z-index: 4;

  ${({ hasError }) =>
    hasError &&
    css`
      background-color: var(--color-error-default);
    `}

  &:hover {
    background-color: var(--color-bg-stronger);
    ${({ hasError }) =>
      hasError &&
      css`
        background-color: var(--color-error-default);
      `}
  }

  ${Icon.S.Icon} {
    fill: var(--color-fg-inverse-default);
  }
`;

const SenderNameInput = styled(Input)`
  font-size: 24px;
  font-size: 16px;
  text-align: center;

  ${({ isEditing }) =>
    !isEditing &&
    css`
      input {
        &:focus {
          border: 1px solid var(--color-components-input-border-default);
        }
      }
    `}

  input {
    color: var(--color-fg-default) !important;
    cursor: default !important;
    font-size: 24px !important;
    min-width: 400px;
    text-align: center;

    ${({ error }) =>
      error &&
      css`
        border: 1px solid var(--color-error-default) !important;
        color: var(--color-error-default) !important;
      `}
  }

  ${({ isMobile }) =>
    isMobile &&
    css`
      input {
        font-size: 18px !important;
        min-width: 340px;
      }
    `}
`;

const GiftMessageWrapper = styled.div`
  height: auto;

  .preview-message {
    background-color: var(--color-bg-weaker2);
    border: 1px solid var(--color-border-default);
    border-radius: var(--size-border-radius-md);
    font-size: 16px;
    line-break: anywhere;
    max-height: 200px;
    min-height: 200px;
    overflow: hidden;
    overflow-y: auto;
    padding: 16px;

    text-align: center;
    width: 400px;

    p {
      line-break: strict;
      min-height: 24px;
    }

    ${({ hasError }) =>
      hasError &&
      css`
        border: 1px solid var(--color-error-default);
        color: var(--color-error-default);
      `}

    ${({ isMobile }) =>
      isMobile &&
      css`
        width: 340px;
      `}
  }
`;

const MessageEditorWrapper = styled.div`
  ${TextEditor.S.Editor} {
    background-color: var(--color-bg-weaker2);
  }
  ${TextEditor.S.TextEditorWrapper} {
    border: none;
    width: 400px;

    ${({ isMobile }) =>
      isMobile &&
      css`
        width: 340px;
      `}
  }

  ${Helper.S.Helper} {
    padding-left: 16px;
  }
`;

const ActionButtons = styled.div`
  bottom: ${({ bottom }) => (bottom ? `${bottom}px` : 0)};
  display: flex;
  gap: 2px;

  justify-content: flex-end;
  position: absolute;
  right: ${({ right }) => (right ? `${right}px` : 0)};

  ${({ top }) =>
    top &&
    css`
      top: ${top}px;
    `}

  ${({ bottom }) =>
    bottom &&
    css`
      bottom: ${bottom}px;
    `}

  ${IconButton.S.Button} {
    background-color: var(--color-bg-default);
    border: 1px solid var(--color-border-default);
    border-radius: var(--size-border-radius-md);
  }
`;

const MessageEditorFooter = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
  justify-content: flex-start;
`;

export {
  ActionButtons,
  DeviceButton,
  DeviceWrapper,
  GiftMessageWrapper,
  MessageEditorFooter,
  MessageEditorWrapper,
  PreviewHeaderWrapper,
  PreviewItem,
  PreviewItemIconButton,
  RecipientExperiencePreviewBody,
  RecipientExperiencePreviewBodyContent,
  RecipientExperiencePreviewBodyFrame,
  RecipientExperiencePreviewHeader,
  RecipientExperiencePreviewWrapper,
  SenderNameInput,
  UploadButton,
  UploadImageButtonWrapper
};
